import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ship, ArrowRight, CheckCircle2, GitCompareArrows, Layers, Timer } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { fmtNum, TRADE_LANE_PAGES } from "@/lib/constants";

const HERO = "https://images.unsplash.com/photo-1585713181935-d5f622cc2415?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMGNvbnRhaW5lciUyMHNoaXAlMjBkcm9uZXxlbnwwfHx8fDE3ODY3MDI0MjJ8MA&ixlib=rb-4.1.0&q=85";

export default function TradeLanePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cfg = TRADE_LANE_PAGES[slug];
  const [rates, setRates] = useState([]);
  const [ids, setIds] = useState({ pol_id: "", pod_id: "" });

  useEffect(() => {
    if (!cfg) { navigate("/rates?shipment_type=export"); return; }
    document.title = cfg.metaTitle;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.setAttribute("content", cfg.metaDescription);
    return () => { document.title = "VK Forwarders"; };
  }, [slug]); // eslint-disable-line

  useEffect(() => {
    if (!cfg) return;
    api.get("/ports").then(({ data }) => {
      const pol = data.find((p) => p.name.toLowerCase() === cfg.pol.toLowerCase());
      const pod = data.find((p) => p.name.toLowerCase() === cfg.pod.toLowerCase());
      const pol_id = pol?.id || "", pod_id = pod?.id || "";
      setIds({ pol_id, pod_id });
      const q = new URLSearchParams({ shipment_type: "export", container_type: "dry" });
      if (pol_id) q.set("pol_id", pol_id);
      if (pod_id) q.set("pod_id", pod_id);
      api.get(`/rates/search?${q.toString()}`).then(({ data }) => {
        const sorted = [...data.results].sort((a, b) => a.estimated_total_usd - b.estimated_total_usd);
        setRates(sorted.slice(0, 8));
      });
    });
  }, [slug]); // eslint-disable-line

  if (!cfg) return null;
  const searchUrl = `/rates?shipment_type=export&container_type=dry${ids.pol_id ? `&pol_id=${ids.pol_id}` : ""}${ids.pod_id ? `&pod_id=${ids.pod_id}` : ""}`;

  return (
    <Layout>
      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt={`${cfg.pol} to ${cfg.pod} container ship`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy/80" />
          <div className="absolute inset-0 hero-grid-fade opacity-50" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 py-16 lg:py-20">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-mono-data text-signal bg-signal/10 border border-signal/30 px-3 py-1 rounded-sm mb-5">
              {cfg.region} · Sea Freight
            </span>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">{cfg.heading}</h1>
            <p className="text-white/70 mt-4 text-base sm:text-lg max-w-2xl">{cfg.blurb}</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to={searchUrl}><Button className="bg-signal hover:bg-signal/90 text-white rounded-sm h-11" data-testid="lane-search-btn">Search All Rates <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
              <Link to="/enquiry"><Button variant="outline" className="border-white/25 text-white bg-transparent hover:bg-white/10 rounded-sm h-11">Plan My Shipment</Button></Link>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12">
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {[
            { icon: GitCompareArrows, t: "Compare Shipping Lines", d: `Carriers serving ${cfg.pol} → ${cfg.pod}, side by side.` },
            { icon: Layers, t: "Ocean Freight + Local Charges", d: "Shown separately, fully transparent." },
            { icon: Timer, t: "Live Validity", d: "Expired rates auto-hidden. Always current." },
          ].map((x) => (
            <div key={x.t} className="border border-border rounded-md p-5 bg-card">
              <x.icon className="h-6 w-6 text-signal" />
              <div className="font-heading font-bold text-navy mt-3">{x.t}</div>
              <div className="text-sm text-muted-foreground mt-1">{x.d}</div>
            </div>
          ))}
        </div>

        <h2 className="font-heading font-bold text-2xl text-navy mb-4">Sample {cfg.pol} → {cfg.pod} FCL Rates</h2>
        {rates.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-10 text-center text-muted-foreground">
            <Ship className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />
            Live rates for this lane are being updated. <Link to={searchUrl} className="text-signal underline">Search all rates</Link>.
          </div>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <div className="hidden sm:grid grid-cols-[1.4fr_1fr_1fr_1fr_0.8fr_auto] gap-2 px-4 py-3 bg-navy text-white text-xs font-semibold uppercase">
              <div>Shipping Line</div><div className="text-right">Ocean Freight</div><div className="text-right">Local Charges</div>
              <div className="text-right">Est. Total</div><div className="text-center">Transit</div><div />
            </div>
            {rates.map((r) => (
              <div key={r.id} className="grid grid-cols-2 sm:grid-cols-[1.4fr_1fr_1fr_1fr_0.8fr_auto] gap-2 px-4 py-3 border-t border-border items-center" data-testid={`lane-rate-${r.id}`}>
                <div className="font-semibold text-navy col-span-2 sm:col-span-1">{r.shipping_line_name} <span className="text-xs font-mono-data text-muted-foreground">· {r.size_label}</span></div>
                <div className="text-right font-mono-data text-sm"><span className="sm:hidden text-muted-foreground text-xs mr-2">Freight</span>USD {fmtNum(r.ocean_freight_usd)}</div>
                <div className="text-right font-mono-data text-sm text-muted-foreground"><span className="sm:hidden text-xs mr-2">Local</span>INR {fmtNum(r.local_charges_inr)}</div>
                <div className="text-right font-mono-data text-sm font-bold text-navy"><span className="sm:hidden text-muted-foreground text-xs mr-2 font-normal">Total</span>USD {fmtNum(r.estimated_total_usd)}</div>
                <div className="text-center font-mono-data text-sm">{r.transit_time}d</div>
                <div className="text-right col-span-2 sm:col-span-1"><Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => navigate(`/rates/${r.id}`)}>View</Button></div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 bg-navy text-white rounded-md p-8 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div>
            <h3 className="font-heading font-extrabold text-2xl">Ready to ship {cfg.pol} → {cfg.pod}?</h3>
            <p className="text-white/60 mt-1 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-signal" />Rates are indicative and confirmed at booking.</p>
          </div>
          <Link to="/enquiry"><Button className="bg-signal hover:bg-signal/90 text-white rounded-sm h-11 px-6">Get Final Quote</Button></Link>
        </div>
      </div>
    </Layout>
  );
}
